/**
 * @fileoverview バージョン情報ページ
 */

import LinkList from "../components/LinkList";
import AtagButton from "../components/AtagButton";
import ScrollToHash from "../components/ScrollToHash";
import { useTranslation } from "react-i18next";
import VersionDetails, {
  VersionDetailsProps,
} from "../components/VersionDetails";

/**
 *
 * @returns {JSX.Element} バージョン情報の JSX element
 */
export const Version: React.FC = (): JSX.Element => {
  //多言語対応用変数
  const { t, i18n } = useTranslation();

  /**
   * リンクリストの項目
   */
  const items = [
    {
      to: "/anasys/HowToInstallDeviceDriver",
      text: t("デバイスドライバーのインストール方法"),
    },
  ];

  /**
   * 旧バージョンリンクリストの項目
   */
  const oldVersionItems = [
    {
      to: "/anasys/Version#Ver56Release",
      text: "Ver5.06.000",
    },
  ];

  /**
   * バージョン情報の項目
   */
  const VersionDetailsItems: VersionDetailsProps[] = [
    {
      version: "Ver6.03.000",
      id: "Ver603000",
      releaseDate: <>{t("2025.03.12")}</>,
      size: <>{t("4,554kB")}</>,
      note:
        i18n.language === "ja" ? (
          <>
            <p className="c-text">
              {t(
                "デザインを全面的に変更し，GHS-8ATとGHS-8AT-EXに対応しています。"
              )}
              <br />
              {t("以下の場合は旧バージョンVer5.06.000を使用してください。")}
            </p>
            <ul className="c-square">
              <li>引き続き今までのデザインで使用したい</li>
              <li>GHS-7A,7ATのロギングデータを読み出したい</li>
              <li>旧アナシスバージョン4.1Jのファイル(*.gtl)を読み込みたい</li>
            </ul>
            <LinkList items={oldVersionItems} />
          </>
        ) : null,
      additionalFeatures: [
        t("ロギングデータ読み取り時に，読み取り異常検出機能を追加"),
      ],
    },
    {
      version: t("デバイスドライバー バージョンアップ"),
      id: "DriverUpdate",
      note: (
        <>
          <p className="c-text">
            {t("デバイスドライバーをバージョンアップしました。")}
            <br />
            Ver2.12.36.4
          </p>
          <LinkList items={items} />
        </>
      ),
    },
  ];

  /**
   * 日本語の時だけ表示したい内容
   */
  const OnlyJapaneseItems: VersionDetailsProps[] = [
    {
      version: "Ver5.06.000",
      id: "Ver56Release",
      releaseDate: "2012.01.30",
      size: "14,149kB",
      note: (
        <>
          <AtagButton
            href="https://gastec-public-soft-download.s3.ap-northeast-1.amazonaws.com/anasys/anasys-installer/V5.06.000/setup.exe"
            text="Ver5.06.000 インストーラー"
          />
          <AtagButton
            href="https://gastec-public-soft-download.s3.ap-northeast-1.amazonaws.com/anasys/manuals/ANASYS5_6MANUAL.pdf"
            text="Ver5.06.000 取扱説明書"
          />
          <ul className="c-square">
            <li>
              Windows 7 64bit, Windows Vista 64bit 動作対応デバイスドライバー
              バージョンアップ(Ver 2.08.14)
            </li>
            <li>デバイスドライバー Windows Updateからの配布</li>
          </ul>
        </>
      ),
    },
    {
      version: "Ver5.05.000",
      releaseDate: "2011.10.06",
      size: "13,665kB",
      additionalFeatures: [
        "CSVファイル変換時にファイル名と識別名を変換データに追加",
      ],
      improvements: [
        "GHS-8ATの検出方法変更 (複数つながっているエラーの対応)",
        "GHS-7A/7AT 温度ロギングデータマイナス温度の不具合修正",
        "グラフ非表示の最大、最小、平均表示の不具合修正",
        "0時台にロギング停止した場合の終了時刻異常値の修正",
        "ロギング間隔1時間での印刷不具合修正",
        "コメント数上限オーバー時の対応変更",
      ],
    },
    {
      version: "Ver5.04.000",
      releaseDate: "2009.04.01",
      additionalFeatures: [
        "温度，濃度，時間軸付近のクリック操作でその軸に対応した表示範囲設定画面が表示",
        "マウスカーソル値のクリック操作での表示，非表示の切替",
        "最大，最小，平均のクリック操作で表示設定画面の表示",
      ],
      improvements: [
        "GHS-7ATとのデータ通信時に一度基礎情報を取得すれば以後は通信が可能",
        "メニュー [編集]-[クリップボード保存]の操作でグラフ表示領域だけが保存されるように変更",
        "GHS-8AT 通信中に別アプリケーションを動作させると画面表示がとまって見える不具合修正",
        "本体が電源オフ状態で通信をするとエラーなる不具合修正",
        "7AT 通信内容修正により無線LAN,USB 変換器接続時のエラー発生頻度の減少",
        "USB ドライバーバージョンアップ(2.04.06G→2.04.14G),ドライバインストール作業の2回発生が1回で終了",
      ],
    },
  ];

  /**
   * 日本語以外の時だけ表示したい内容
   */
  const OtherThanJapanItems: VersionDetailsProps[] = [
    {
      version: "6.02.000",
      releaseDate: t("04.26.2019"),
      size: "53,491kB",
      additionalFeatures: [
        t(
          "For GHS-8AT-EX version 2.1 or later, select the displayed temperature in Celsius or Fahrenheit."
        ),
        t(
          "Set the unit of temperature displayed in the graph and CSV file(degC or degF)."
        ),
      ],
    },
    {
      version: "6.01.001",
      releaseDate: t("30.08.2017"),
      size: "53,510kB",
      note: (
        <ul className="c-square">
          <li>{t("First edition issued")}</li>
        </ul>
      ),
    },
  ];

  if (i18n.language === "ja") {
    VersionDetailsItems.push(...OnlyJapaneseItems);
  } else {
    VersionDetailsItems.push(...OtherThanJapanItems);
  }

  return (
    <section className="c-section">
      <ScrollToHash />

      <h2 className="p-heading-2">{t("バージョン情報")}</h2>
      {VersionDetailsItems.map((item, index) => (
        <VersionDetails
          version={item.version}
          id={item.id}
          releaseDate={item.releaseDate}
          size={item.size}
          note={item.note}
          additionalFeatures={item.additionalFeatures}
          improvements={item.improvements}
        />
      ))}
    </section>
  );
};
